import createCache from "@emotion/cache";
import { CacheProvider, ThemeProvider } from "@emotion/react";
import { Box, Button, FormControl } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import ChipCmp from "../../../components/chip/chipCmp";
import DatePickerComponents from "../../../components/datePicker/DatePickerComponents";
import AutocomplateCheckbox from "../../../components/dropdown/AutocomplateCheckbox";
import MaterialTextFieldComponent from "../../../components/textField/MaterialTextFieldCmp";
import { SettlemetRequestToApproveFilter } from "../../../models/settlement.model";
import { AllStoresAndBranchesResponse } from "../../../models/supplierUserBarnch.model";
import { convertDate } from "../../../utility/convertDate";

const theme = createTheme({
  direction: "rtl", // Both here and <body dir="rtl">
});
// Create rtl cache
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

interface Props {
  setFilterObject: (filterObj: SettlemetRequestToApproveFilter) => void;
  branchesOptions: AllStoresAndBranchesResponse[];
  filterObject: SettlemetRequestToApproveFilter;
  setCurrentPaginationInfo: (x: any) => void;
}
const SettlementRequestsListFilter = ({
  setFilterObject,
  branchesOptions,
  filterObject,
  setCurrentPaginationInfo,
}: Props) => {
  const [formData, setFormData] = useState<SettlemetRequestToApproveFilter>();
  const [saleDateFrom, setSaleDateFrom] = useState<Date[] | null | undefined>(
    null
  );
  const [waitForSettlementFrom, setWaitForSettlementFrom] = useState<
    Date[] | null | undefined
  >(null);
  const [settlementDeadlineFrom, setSettlementDeadlineFrom] = useState<
    Date[] | null | undefined
  >(null);
  const [valueSaleDateFrom, setValueSaleDateFrom] = useState<string>();
  const [valueWaitForSettlementFrom, setValueWaitForSettlementFrom] = useState<
    string
  >();
  const [
    valueSettlementDeadlineFrom,
    setValueSettlementDeadlineFrom,
  ] = useState<string>();
  const [valueBranchesOptions, setValueBranchesOptions] = useState<number[]>(
    []
  );

  const [valueBranches, setValueBranches] = useState<any[]>([]);

  const queryClient = useQueryClient();

  function changeSaleDateFrom(e: any) {
    setValueSaleDateFrom(e.toString().replace(/,/g, " - "));
    setSaleDateFrom(e);
  }
  function changeWaitForSettlementFrom(e: any) {
    setValueWaitForSettlementFrom(e.toString().replace(/,/g, " - "));
    setWaitForSettlementFrom(e);
  }
  function changeSettlementDeadlineFrom(e: any) {
    setValueSettlementDeadlineFrom(e.toString().replace(/,/g, " - "));
    setSettlementDeadlineFrom(e);
  }

  const setChildBranch = (chip: any) => {
    const selectedItem = valueBranches.filter(
      (item: any) => item.title !== chip
    );
    const reamaningItems = valueBranchesOptions.filter(
      (item: any) => item !== chip
    );
    setValueBranches(selectedItem);
    setValueBranchesOptions(reamaningItems.map((item: any) => item));
    const tabelFilter = queryClient.getQueryData<
      SettlemetRequestToApproveFilter
    >("SettlementRequestListFilter");

    if (tabelFilter !== undefined && tabelFilter != null) {
      tabelFilter!.branchStoreIds = tabelFilter!.branchStoreIds
        ?.filter((item: any) => item.title !== chip)
        .map((items: any) => {
          return items;
        });
    }

    setFilterObject({
      ...filterObject,
      branchStoreIds: selectedItem,
    });
  };

  const setChildCustomerName = () => {
    let tableFilter = queryClient.getQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter"
    );
    tableFilter!.customerName = "";
    setFormData({ ...formData!, customerName: "" });
    queryClient.setQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter",
      tableFilter!
    );
    setFilterObject(tableFilter!);
  };
  const setChildCustomerNationalCode = () => {
    let tableFilter = queryClient.getQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter"
    );
    tableFilter!.customerNationalCode = "";
    setFormData({ ...formData!, customerNationalCode: "" });
    queryClient.setQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter",
      tableFilter!
    );
    setFilterObject(tableFilter!);
  };
  const setChildCustomerMobileNumber = () => {
    let tableFilter = queryClient.getQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter"
    );
    tableFilter!.customerMobileNumber = "";
    setFormData({ ...formData!, customerMobileNumber: "" });
    queryClient.setQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter",
      tableFilter!
    );
    setFilterObject(tableFilter!);
  };
  const setChildSaleDate = () => {
    let tableFilter = queryClient.getQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter"
    );
    tableFilter = { ...tableFilter!, saleDateFrom: undefined };
    queryClient.setQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter",
      tableFilter!
    );
    setSaleDateFrom(undefined);
    setValueSaleDateFrom(undefined);
    setFilterObject(tableFilter!);
  };
  const setChildWaitForSettlement = () => {
    let tableFilter = queryClient.getQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter"
    );
    tableFilter = { ...tableFilter!, waitForSettlementFrom: undefined };
    queryClient.setQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter",
      tableFilter!
    );
    setWaitForSettlementFrom(undefined);
    setValueWaitForSettlementFrom(undefined);
    setFilterObject(tableFilter!);
  };
  const setChildSettlementDeadline = () => {
    let tableFilter = queryClient.getQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter"
    );
    tableFilter = { ...tableFilter!, settlementDeadlineFrom: undefined };
    queryClient.setQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter",
      tableFilter!
    );
    setSettlementDeadlineFrom(undefined);
    setValueSettlementDeadlineFrom(undefined);
    setFilterObject(tableFilter!);
  };

  const handleChangeForm = (event: any) => {
    setFormData({ ...formData!, [event.target.id]: event.target.value });
    queryClient.setQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter",
      { ...formData!, [event.target.id]: event.target.value }
    );
  };
  function changeValueBranchesOptions(e: any, value: any) {
    setValueBranchesOptions(value.map((item: any) => item.title));
    setValueBranches([...e]);
  }

  const deleteChip = () => {
    let tableFilter = queryClient.getQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter"
    );
    tableFilter = { ...tableFilter!, branchStoreIds: [] };
    tableFilter.customerMobileNumber = "";
    tableFilter.customerNationalCode = "";
    tableFilter.customerName = "";
    tableFilter.saleDateFrom = undefined;
    tableFilter.waitForSettlementFrom = undefined;
    tableFilter.settlementDeadlineFrom = undefined;
    setValueBranches([]);
    setValueBranchesOptions([]);
    setSaleDateFrom(null);
    setValueSaleDateFrom(undefined);
    setWaitForSettlementFrom(null);
    setValueWaitForSettlementFrom(undefined);
    setSettlementDeadlineFrom(null);
    setValueSettlementDeadlineFrom(undefined);
    setFormData({
      ...formData,
      customerName: "",
      customerNationalCode: "",
      customerMobileNumber: "",
    });
    queryClient.setQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter",
      tableFilter
    );
    setFilterObject(tableFilter!);
  };

  const handleFormSubmit = () => {
    let tableFilter = queryClient.getQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter"
    );
    let branchIds: number[] = [];
    let storeIds: number[] = [];
    valueBranches.map((item) =>
      item.childId === null
        ? storeIds.push(item.parentId)
        : branchIds.push(item.childId)
    );

    const convertedSaleDate = convertDate(saleDateFrom);
    const convertedWaitForSettlement = convertDate(waitForSettlementFrom);
    const convertedSettlementDeadline = convertDate(settlementDeadlineFrom);

    tableFilter = {
      ...tableFilter!,
      saleDateFrom: Array.isArray(convertedSaleDate)
        ? convertedSaleDate[0]
        : convertedSaleDate,
      saleDateTo: Array.isArray(convertedSaleDate)
        ? convertedSaleDate[1]
        : undefined,
      waitForSettlementFrom: Array.isArray(convertedWaitForSettlement)
        ? convertedWaitForSettlement[0]
        : convertedWaitForSettlement,
      waitForSettlementTo: Array.isArray(convertedWaitForSettlement)
        ? convertedWaitForSettlement[1]
        : undefined,
      settlementDeadlineFrom: Array.isArray(convertedSettlementDeadline)
        ? convertedSettlementDeadline[0]
        : convertedSettlementDeadline,
      settlementDeadlineTo: Array.isArray(convertedSettlementDeadline)
        ? convertedSettlementDeadline[1]
        : undefined,
      branchStoreIds: valueBranches,
    };
    queryClient.setQueryData<SettlemetRequestToApproveFilter>(
      "SettlementRequestListFilter",
      tableFilter
    );
    setCurrentPaginationInfo({
      pageNumber: 1,
      pageSize: 10,
    });
    setFilterObject(tableFilter);
  };

  return (
    <div>
      <Box sx={{ flexGrow: 1 }}>
        <div className='row'>
          <div
            className='col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12 dropdownCmp2-container '
            style={{ marginTop: "1vh" }}
          >
            <CacheProvider value={cacheRtl}>
              <ThemeProvider theme={theme}>
                {branchesOptions && (
                  <div dir='rtl'>
                    <AutocomplateCheckbox
                      id='branches'
                      name='branches'
                      value={valueBranchesOptions}
                      label={"فروشنده"}
                      options={branchesOptions}
                      onChange={changeValueBranchesOptions}
                      changeMultiSelect={changeValueBranchesOptions}
                      responseValue={valueBranches}
                    />
                  </div>
                )}
              </ThemeProvider>
            </CacheProvider>
          </div>
          <div
            className='col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12'
            style={{ marginTop: "1vh" }}
          >
            <MaterialTextFieldComponent
              // className="shadowTextField"
              helperText={""}
              placeHolder={"نام متقاضی"}
              label={"نام متقاضی"}
              required={false}
              value={formData?.customerName}
              onChange={(e: any) => handleChangeForm(e)}
              name='customerName'
              id='customerName'
            />
          </div>
          <div
            className='col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12'
            style={{ marginTop: "1vh" }}
          >
            <MaterialTextFieldComponent
              // className="shadowTextField"
              helperText={""}
              placeHolder={"کد ملی متقاضی"}
              label={"کد ملی متقاضی"}
              required={false}
              value={formData?.customerNationalCode}
              onChange={(e: any) => handleChangeForm(e)}
              name='customerNationalCode'
              id='customerNationalCode'
            />
          </div>
          <div
            className='col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12'
            style={{ marginTop: "1vh" }}
          >
            <MaterialTextFieldComponent
              // className="shadowTextField"
              helperText={""}
              placeHolder={"شماره موبایل"}
              label={"شماره موبایل"}
              required={false}
              value={formData?.customerMobileNumber}
              onChange={(e: any) => handleChangeForm(e)}
              name='customerMobileNumber'
              id='customerMobileNumber'
            />
          </div>
          <div
            className='col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12'
            style={{ marginTop: "1vh" }}
          >
            <FormControl fullWidth className='shadowDatepicker'>
              <DatePickerComponents
                valueArray={saleDateFrom}
                // value={new Date(valueRequestDateFrom!)}
                label={"تاریخ فروش"}
                onChange={changeSaleDateFrom}
                range={true}
              />
            </FormControl>
          </div>
          <div
            className='col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12'
            style={{ marginTop: "1vh" }}
          >
            <FormControl fullWidth className='shadowDatepicker'>
              <DatePickerComponents
                valueArray={waitForSettlementFrom}
                // value={new Date(valueRequestDateFrom!)}
                label={"تاریخ آماده به تسویه"}
                onChange={changeWaitForSettlementFrom}
                range={true}
              />
            </FormControl>
          </div>
          <div
            className='col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12'
            style={{ marginTop: "1vh" }}
          >
            <FormControl fullWidth className='shadowDatepicker'>
              <span style={{ zIndex: "200" }}>
                <DatePickerComponents
                  valueArray={settlementDeadlineFrom}
                  // value={new Date(valueRequestDateFrom!)}
                  label={"مهلت تسویه"}
                  onChange={changeSettlementDeadlineFrom}
                  range={true}
                />
              </span>
            </FormControl>
          </div>

          <div
            className=' col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12 butFilter'
            style={{ marginTop: "1vh" }}
          >
            <Button
              fullWidth
              variant='contained'
              // type="submit"
              className='btnForm SuccessDefault  '
              onClick={handleFormSubmit}
            >
              اعمال فیلتر
            </Button>
          </div>
        </div>
        {(formData?.customerMobileNumber ||
          formData?.customerName ||
          formData?.customerNationalCode ||
          valueSaleDateFrom ||
          valueWaitForSettlementFrom ||
          valueBranchesOptions?.length ||
          valueSettlementDeadlineFrom) && (
          <div
            style={{ display: "flex", alignItems: "center" }}
            className='row tableCMP'
          >
            <Button
              onClick={deleteChip}
              style={{
                justifyContent: "right",
                minWidth: "25px",
                marginRight: "-1vh",
                marginTop: "1vh",
              }}
            >
              <img
                style={{ height: "24px", width: "24px" }}
                src='images/RemoveAllFilters.svg'
                alt=''
              />
            </Button>
            {valueBranchesOptions.map((title, index) => (
              <ChipCmp
                item={title}
                enamValue={title}
                index={index}
                setChildValue={setChildBranch}
              />
            ))}

            <ChipCmp
              item={formData?.customerName}
              enamValue={formData?.customerName}
              index={formData?.customerName?.length}
              setChildValue={setChildCustomerName}
            />
            <ChipCmp
              item={formData?.customerNationalCode}
              enamValue={formData?.customerNationalCode}
              index={formData?.customerNationalCode?.length}
              setChildValue={setChildCustomerNationalCode}
            />
            <ChipCmp
              item={formData?.customerMobileNumber}
              enamValue={formData?.customerMobileNumber}
              index={formData?.customerMobileNumber?.length}
              setChildValue={setChildCustomerMobileNumber}
            />
            <ChipCmp
              item={valueSaleDateFrom}
              enamValue={valueSaleDateFrom}
              index={valueSaleDateFrom?.length}
              setChildValue={setChildSaleDate}
            />
            <ChipCmp
              item={valueWaitForSettlementFrom}
              enamValue={valueWaitForSettlementFrom}
              index={valueWaitForSettlementFrom?.length}
              setChildValue={setChildWaitForSettlement}
            />
            <ChipCmp
              item={valueSettlementDeadlineFrom}
              enamValue={valueSettlementDeadlineFrom}
              index={valueSettlementDeadlineFrom?.length}
              setChildValue={setChildSettlementDeadline}
            />
          </div>
        )}
      </Box>
    </div>
  );
};

export default SettlementRequestsListFilter;
